import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ClinicCombobox } from '@/components/ClinicCombobox';
import { toast } from '@/components/ui/toaster';
import { useMovePatientClinic } from '@/hooks/queries';
import { ANALYTICS_EVENTS, track } from '@/lib/analytics';
import type { ClinicResponse } from '@/api/hospital';
import type { PatientListRow } from '@/api/patients';

type Props = {
  open: boolean;
  onClose: () => void;
  row: PatientListRow;
  /** Every clinic the current clinician belongs to (all hospitals). */
  clinicianClinics: ClinicResponse[];
  /** Fallback home hospital when a clinic response omits hospital_id. */
  homeHospitalId?: number | null;
};

/**
 * FOOD-651 — move a patient from one clinic to another in the **same hospital**.
 * Destination options are clinician clinics that share the source clinic's
 * hospital_id; cross-hospital options are never shown.
 */
export const MovePatientClinicDialog = ({
  open,
  onClose,
  row,
  clinicianClinics,
  homeHospitalId,
}: Props) => {
  const { t } = useTranslation();
  const movePatient = useMovePatientClinic();

  const patientName = `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim();

  /** Patient clinics the clinician also belongs to (intersection by id). */
  const sharedPatientClinics = useMemo(() => {
    const clinicianIds = new Set(clinicianClinics.map((c) => c.id));
    return (row.clinics ?? []).filter((c) => clinicianIds.has(c.id));
  }, [row.clinics, clinicianClinics]);

  const [fromClinicId, setFromClinicId] = useState<number | null>(null);
  const [toClinicId, setToClinicId] = useState<number | null>(null);
  const [moveReports, setMoveReports] = useState(true);
  const [moveLoggingPeriods, setMoveLoggingPeriods] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Seed / reset when the dialog opens or closes.
  useEffect(() => {
    if (!open) {
      setFromClinicId(null);
      setToClinicId(null);
      setMoveReports(true);
      setMoveLoggingPeriods(true);
      setError(null);
      return;
    }
    setFromClinicId(sharedPatientClinics[0]?.id ?? row.clinics?.[0]?.id ?? null);
    setToClinicId(null);
    setMoveReports(true);
    setMoveLoggingPeriods(true);
    setError(null);
  }, [open, sharedPatientClinics, row.clinics]);

  const resolveHospitalId = (clinicId: number | null): number | undefined => {
    if (clinicId == null) return undefined;
    const clinic = clinicianClinics.find((c) => c.id === clinicId);
    if (clinic?.hospital_id != null) return clinic.hospital_id;
    return homeHospitalId ?? undefined;
  };

  const sourceHospitalId = resolveHospitalId(fromClinicId);

  /** Same-hospital destinations only — never other hospitals. */
  const destinationClinics = useMemo(() => {
    if (fromClinicId == null || sourceHospitalId == null) return [];
    return clinicianClinics.filter((c) => {
      if (c.id === fromClinicId) return false;
      const hid = c.hospital_id ?? homeHospitalId ?? undefined;
      return hid === sourceHospitalId;
    });
  }, [clinicianClinics, fromClinicId, sourceHospitalId, homeHospitalId]);

  // Clear destination if it falls out of the filtered set (e.g. source change).
  useEffect(() => {
    if (toClinicId == null) return;
    if (!destinationClinics.some((c) => c.id === toClinicId)) {
      setToClinicId(null);
    }
  }, [destinationClinics, toClinicId]);

  const fromClinicName =
    sharedPatientClinics.find((c) => c.id === fromClinicId)?.name
    ?? row.clinics?.find((c) => c.id === fromClinicId)?.name
    ?? clinicianClinics.find((c) => c.id === fromClinicId)?.name
    ?? t('this clinic');

  const toClinicName =
    destinationClinics.find((c) => c.id === toClinicId)?.name ?? '';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (fromClinicId == null || toClinicId == null || sourceHospitalId == null) {
      setError(t('Select a clinic'));
      return;
    }
    const move: Array<'reports' | 'subscriptions'> = [];
    if (moveReports) move.push('reports');
    if (moveLoggingPeriods) move.push('subscriptions');

    try {
      await movePatient.mutateAsync({
        hospitalId: sourceHospitalId,
        patientId: row.patient_id,
        body: {
          from_clinic_id: fromClinicId,
          to_clinic_id: toClinicId,
          // Both checked → omit (backend default = all). Empty = membership only.
          ...(move.length === 2 ? {} : { move }),
        },
      });
      toast({
        title: t('Moved {{name}} to {{clinic}}', {
          name: patientName,
          clinic: toClinicName,
        }),
      });
      track(ANALYTICS_EVENTS.PATIENT_MOVED_CLINIC, {
        from_clinic_id: fromClinicId,
        to_clinic_id: toClinicId,
        move_reports: moveReports,
        move_logging_periods: moveLoggingPeriods,
      });
      onClose();
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message
        ?? t('Could not move patient');
      setError(msg);
      toast({ title: t('Could not move patient'), variant: 'destructive', description: msg });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] w-[min(96vw,460px)] max-w-[96vw] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('Move patient to another clinic')}</DialogTitle>
          <DialogDescription>
            {t(
              'Move patient to another clinic in the same hospital. Reports and logging periods can move with them.',
            )}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          {sharedPatientClinics.length > 1 ? (
            <div className="space-y-1">
              <Label htmlFor="move-from-clinic">{t('Current clinic')}</Label>
              <select
                id="move-from-clinic"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={fromClinicId ?? ''}
                onChange={(e) => setFromClinicId(Number(e.target.value) || null)}
              >
                {sharedPatientClinics.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="space-y-1">
              <Label>{t('Current clinic')}</Label>
              <p className="text-sm text-muted-foreground">{fromClinicName}</p>
            </div>
          )}

          <div className="space-y-1">
            <Label>{t('Destination clinic')}</Label>
            <ClinicCombobox
              clinics={destinationClinics}
              value={toClinicId}
              onChange={setToClinicId}
              placeholder={t('Select a clinic')}
              disabled={destinationClinics.length === 0}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={moveReports}
                onChange={(e) => setMoveReports(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 rounded"
              />
              <span>
                <span className="font-medium">{t('Move reports')}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {t('Move reports — existing reports will appear under the new clinic')}
                </span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={moveLoggingPeriods}
                onChange={(e) => setMoveLoggingPeriods(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 rounded"
              />
              <span>
                <span className="font-medium">{t('Move logging periods')}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {t(
                    'Move logging periods — active and past logging periods (and any repeating schedule) continue under the new clinic',
                  )}
                </span>
              </span>
            </label>
          </div>

          {fromClinicId != null && toClinicId != null && (
            <p className="text-sm text-muted-foreground">
              {t('Move {{name}} from {{from}} to {{to}}?', {
                name: patientName,
                from: fromClinicName,
                to: toClinicName,
              })}
            </p>
          )}

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={movePatient.isPending}>
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={movePatient.isPending || toClinicId == null || fromClinicId == null}
            >
              {movePatient.isPending ? t('Moving…') : t('Move patient')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

